import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge, Tooltip } from '@wit-support/ui';
import { Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { CONTENT_ALERT_SEVERITY_BADGE_VARIANT, CONTENT_ALERT_SEVERITY_LABEL_RU, CONTENT_LEGACY_COMPOSE_HINT_RU, CONTENT_LEGACY_COMPOSE_LABEL_RU, CONTENT_MATURING_HINT_RU, CONTENT_MATURING_LABEL_RU, CONTENT_RESTING_LABEL_RU, CONTENT_TIER_LABEL_RU, CONTENT_TREND_LABEL_RU, } from './content-labels.js';
import { cn } from './lib/cn.js';
import { formatMultiple } from './lib/format-multiple.js';
import { Time } from './time.js';
import { CONTENT_MULTIPLE_TONE_CLASS, CONTENT_TIER_BADGE_VARIANT, CONTENT_TREND_TONE_CLASS, contentMultipleTone, } from './tokens/content.js';
/** Ярус силы клипа цветом: сильный — зелёный, рабочий — синий, слабый — серый. */
export function ContentTierBadge({ tier, size }) {
    return (_jsx(Badge, { variant: CONTENT_TIER_BADGE_VARIANT[tier], "data-tier": tier, ...(size ? { size } : {}), children: CONTENT_TIER_LABEL_RU[tier] }));
}
/** Уровень тревоги контент-движка: подпись и цвет из одной карты. */
export function ContentAlertSeverityBadge({ severity, size, }) {
    return (_jsx(Badge, { variant: CONTENT_ALERT_SEVERITY_BADGE_VARIANT[severity], ...(size ? { size } : {}), children: CONTENT_ALERT_SEVERITY_LABEL_RU[severity] }));
}
/**
 * «набирает» — ролик ещё набирает просмотры, кратность предварительная.
 *
 * `withHint` — пояснение во всплывающей подсказке кита (а не `title`, который
 * виден только мышью): бейдж фокусируется с клавиатуры и показывает подсказку
 * на фокус. Без `withHint` экран объясняет это рядом сам (карточка публикации).
 */
export function ContentMaturingBadge({ withHint = false }) {
    if (!withHint)
        return _jsx(Badge, { variant: "warning", children: CONTENT_MATURING_LABEL_RU });
    return (_jsx(Tooltip.Provider, { children: _jsxs(Tooltip.Root, { children: [_jsx(Tooltip.Trigger, { asChild: true, children: _jsx(Badge, { variant: "warning", tabIndex: 0, children: CONTENT_MATURING_LABEL_RU }) }), _jsx(Tooltip.Content, { children: CONTENT_MATURING_HINT_RU })] }) }));
}
/**
 * «Старая склейка» — движок не смог собрать ролик, вышел ролик старой
 * склейки. Подсказка в тултипе кита (фокусируется с клавиатуры). Отличается от
 * причины выбора «запасной вариант» (все клипы на паузе).
 */
export function ContentLegacyComposeBadge() {
    return (_jsx(Tooltip.Provider, { children: _jsxs(Tooltip.Root, { children: [_jsx(Tooltip.Trigger, { asChild: true, children: _jsx(Badge, { variant: "neutral", tabIndex: 0, children: CONTENT_LEGACY_COMPOSE_LABEL_RU }) }), _jsx(Tooltip.Content, { children: CONTENT_LEGACY_COMPOSE_HINT_RU })] }) }));
}
/**
 * «пауза до 12.10» — клип недавно шёл первым и до этой даты первым не ставится.
 * Дата — в таймзоне админа (общий `Time`), а не в UTC: иначе вечером по
 * Азии бейдж показывал бы предыдущий день.
 */
export function ContentRestingBadge({ until }) {
    return (_jsxs(Badge, { variant: "outline", children: [CONTENT_RESTING_LABEL_RU, " ", _jsx(Time, { iso: until, preset: "dayMonthNumeric" })] }));
}
const TREND_ICON = {
    up: TrendingUp,
    down: TrendingDown,
    flat: Minus,
};
/**
 * Тренд обычного ролика аккаунта значком; смысл — в `aria-label` из
 * `CONTENT_TREND_LABEL_RU`. Цвет: рост — зелёный, падение — красный, без
 * изменений — серый (`toned={false}` — всё серым, как раньше).
 */
export function ContentTrendIcon({ trend, className, toned = true, }) {
    if (trend === null)
        return null;
    const Icon = TREND_ICON[trend];
    return (_jsx(Icon, { "data-trend": trend, className: cn('h-4 w-4', toned ? CONTENT_TREND_TONE_CLASS[trend] : 'text-[var(--color-text-muted)]', className), "aria-label": `Тренд: ${CONTENT_TREND_LABEL_RU[trend]}`, role: "img" }));
}
/** Кратность «×1,8» с украшениями: цвет по порогам, значок тренда и/или бейдж «набирает». */
export function ContentMultiple({ value, trend, maturing = false, toned = true, }) {
    const tone = toned && !maturing ? contentMultipleTone(value) : null;
    return (_jsxs("span", { className: "inline-flex items-center gap-1.5 tabular-nums", children: [_jsx("span", { ...(tone ? { 'data-tone': tone } : {}), className: tone ? CONTENT_MULTIPLE_TONE_CLASS[tone] : undefined, children: formatMultiple(value) }), trend !== undefined ? _jsx(ContentTrendIcon, { trend: trend }) : null, maturing ? _jsx(ContentMaturingBadge, { withHint: true }) : null] }));
}
//# sourceMappingURL=content-badges.js.map